<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { yyyy, zzzz } from "../../../store.svelte";
	import { diffState } from "../../../charts/extras/chronicleDiff.svelte";
	import Fa from "svelte-fa";
	import { faTimes } from "@fortawesome/free-solid-svg-icons";

	const url: string = yyyy.uri.api;

	let loading = $state(true);
	let chain: any[] = $state([]);
	let selectedIndex: number | null = $state(null);

	const close = () => {
		zzzz.emit("chronicle-diff-hide");
		yyyy.ctrl.control = undefined;
		yyyy.ctrl.expanded = false;
	};

	function selectEntry(index: number) {
		if (index === 0) return; // root has no predecessor to compare against
		const entry = chain[index];
		const predecessor = chain[index - 1];
		selectedIndex = index;
		zzzz.emit("chronicle-diff-show", {
			oldWireident: predecessor.wireident,
			newWireident: entry.wireident,
		});
	}

	async function fetchChain() {
		loading = true;
		const wireident = yyyy.sesh.activeLivewire?.wireident;
		if (!wireident) { loading = false; return; }

		try {
			const res = await fetch(`${url}/chronicle?select=*`, {
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
			});
			if (!res.ok) throw new Error("Failed to fetch chronicle");
			const allEntries: any[] = await res.json();

			// Build maps for traversal
			const byId = new Map(allEntries.map(e => [e.livewire_id, e]));
			const byPredecessor = new Map(allEntries.map(e => [e.predecessor, e]));

			// Walk backwards from active wireident to find the root
			let root = wireident;
			let entry = byId.get(root);
			while (entry?.predecessor) {
				root = entry.predecessor;
				entry = byId.get(root);
			}

			// Root is the original livewire (no predecessor entry)
			chain = [];
			const rootLw = yyyy.livewires?.find((lw: any) => lw.wireident === root);
			chain.push({
				wireident: root,
				livewire: rootLw?.livewire || root.slice(0, 8),
				isRoot: true,
				changes: null,
				created_at: rootLw?.created_at,
				isReady: rootLw?.isready,
			});

			// Walk forwards through successors
			let current: string | null = root;
			let successor = byPredecessor.get(current);
			while (successor) {
				const lw = yyyy.livewires?.find((lw: any) => lw.wireident === successor.livewire_id);
				chain.push({
					wireident: successor.livewire_id,
					livewire: lw?.livewire || successor.livewire_id.slice(0, 8),
					isRoot: false,
					changes: successor.changes,
					created_at: successor.created_at,
					isReady: lw?.isready,
				});
				current = successor.livewire_id;
				successor = byPredecessor.get(current);
			}
		} catch (err) {
			console.error("Chronicle fetch error:", err);
		} finally {
			loading = false;
		}
	}

	// Total changes across all layers for a given entry
	function totalChanges(changes: any): { added: number; removed: number; modified: number } {
		let added = 0, removed = 0, modified = 0;
		for (const counts of Object.values(changes) as any[]) {
			added += counts.added || 0;
			removed += counts.removed || 0;
			modified += counts.modified || 0;
		}
		return { added, removed, modified };
	}

	onMount(async () => {
		await fetchChain();
		// Auto-select the active livewire to show its diff on the map
		const activeIdx = chain.findIndex(
			(e) => e.wireident === yyyy.sesh.activeLivewire?.wireident
		);
		if (activeIdx > 0) selectEntry(activeIdx);
	});

	onDestroy(() => {
		zzzz.emit("chronicle-diff-hide");
	});
</script>

<div id="chronicle">
	<div class="panel-header">
		<button class="close-btn" onclick={close}>
			<Fa icon={faTimes} size="1.75x" />
		</button>
		<span class="panel-title">Chronicle</span>
	</div>

	{#if loading}
		<div class="loading-container">
			<div class="spinner"></div>
			<p class="loading-text">Loading history...</p>
		</div>
	{:else if chain.length <= 1}
		<div class="empty-state">
			<p>No version history for this livewire.</p>
		</div>
	{:else}
		<div class="timeline">
			{#each chain as entry, i}
				{@const isActive = entry.wireident === yyyy.sesh.activeLivewire?.wireident}
				{@const isSelected = selectedIndex === i}
				<div
					class="timeline-entry"
					class:active={isActive}
					class:selected={isSelected}
					class:clickable={i > 0}
					onclick={() => selectEntry(i)}
				>
					<div class="timeline-marker">
						<div class="marker-dot" class:active={isActive} class:selected={isSelected}></div>
						{#if i < chain.length - 1}
							<div class="marker-line"></div>
						{/if}
					</div>
					<div class="timeline-content">
						<div class="entry-header">
							<span class="entry-name" class:active={isActive}>{entry.livewire}</span>
							{#if entry.isRoot}
								<span class="badge root-badge">Origin</span>
							{/if}
							{#if entry.isReady === false}
								<span class="badge generating-badge">Generating</span>
							{/if}
						</div>
						{#if entry.created_at}
							<span class="entry-date">{new Date(entry.created_at).toLocaleDateString()}</span>
						{/if}
						{#if entry.changes}
							{@const totals = totalChanges(entry.changes)}
							<div class="changes-summary">
								{#each Object.entries(entry.changes) as [table, counts]}
									{@const c = counts as any}
									<div class="change-row">
										<span class="change-table">{table}</span>
										<div class="change-badges">
											{#if c.added > 0}
												<span class="change-badge added">+{c.added}</span>
											{/if}
											{#if c.removed > 0}
												<span class="change-badge removed">-{c.removed}</span>
											{/if}
											{#if c.modified > 0}
												<span class="change-badge modified">&plusmn;{c.modified}</span>
											{/if}
										</div>
									</div>
								{/each}
								<div class="change-totals">
									{#if totals.added > 0}<span class="change-badge added">+{totals.added}</span>{/if}
									{#if totals.removed > 0}<span class="change-badge removed">-{totals.removed}</span>{/if}
									{#if totals.modified > 0}<span class="change-badge modified">&plusmn;{totals.modified}</span>{/if}
								</div>
							</div>
						{/if}
					</div>
				</div>
			{/each}
		</div>

		{#if selectedIndex !== null}
			<div class="diff-list-section">
				<div class="diff-list-header">
					<span class="diff-list-title">Changes in viewport</span>
					{#if diffState.loading}
						<span class="diff-loading-indicator"></span>
					{:else}
						<span class="diff-list-count">{diffState.features.length}</span>
					{/if}
				</div>
				{#if diffState.features.length > 0}
					<div class="diff-list">
						{#each diffState.features as f}
							<div class="diff-item {f.diff_status}">
								<span class="diff-status-icon {f.diff_status}">{#if f.diff_status === 'added'}+{:else if f.diff_status === 'removed'}&minus;{:else}&plusmn;{/if}</span>
								<div class="diff-item-info">
									<span class="diff-item-table">{f.lw_table}</span>
									<span class="diff-item-pkid">{f.lw_table_pkid}</span>
									{#if f.diff_detail}
										<span class="diff-item-detail">
											{#each Object.entries(f.diff_detail) as [col, vals]}
												{@const v = vals as any}
												{#if col !== 'geom_changed'}
													<span class="diff-field">{col}: {v.old} &rarr; {v.new}</span>
												{:else if v.new === true}
													<span class="diff-field">geometry moved</span>
												{/if}
											{/each}
										</span>
									{/if}
								</div>
								<span class="diff-item-type">{f.feature_type}</span>
							</div>
						{/each}
					</div>
				{:else if !diffState.loading}
					<div class="diff-empty">No changes in current viewport</div>
				{/if}
			</div>
		{/if}
	{/if}
</div>

<style>
	#chronicle {
		width: 100%;
		padding: 8.775px;
	}

	.panel-header {
		display: flex;
		align-items: center;
		min-height: 45px;
		gap: 0.5rem;
	}

	.close-btn {
		background: none;
		border: none;
		cursor: pointer;
		color: #ff3e00;
		padding: 0;
	}

	.close-btn:hover {
		opacity: 0.7;
	}

	.panel-title {
		font-weight: bolder;
		padding-top: 2px;
		flex: 1;
	}

	.loading-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 2rem;
		gap: 1rem;
	}

	.spinner {
		width: 40px;
		height: 40px;
		border: 3px solid rgba(102, 126, 234, 0.1);
		border-top: 3px solid #667eea;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}

	.loading-text {
		color: #667eea;
		font-size: 0.9rem;
		font-weight: 500;
		margin: 0;
	}

	@keyframes spin {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}

	.empty-state {
		text-align: center;
		padding: 2rem 1rem;
		color: #999;
	}

	/* Timeline */
	.timeline {
		padding: 0.5rem 0.5rem 1rem 0.5rem;
	}

	.timeline-entry {
		display: flex;
		gap: 1rem;
		position: relative;
	}

	.timeline-marker {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 20px;
		flex-shrink: 0;
	}

	.marker-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
		border: 2px solid #ccc;
		background: white;
		z-index: 1;
		margin-top: 4px;
	}

	.marker-dot.active {
		border-color: #ff3e00;
		background: #ff3e00;
	}

	.marker-dot.selected {
		border-color: #3273dc;
		background: #3273dc;
		box-shadow: 0 0 0 3px rgba(50, 115, 220, 0.3);
	}

	.timeline-entry.clickable {
		cursor: pointer;
	}

	.timeline-entry.clickable:hover .timeline-content {
		background: #f7fafc;
		border-radius: 4px;
	}

	.timeline-entry.selected .timeline-content {
		background: rgba(50, 115, 220, 0.05);
		border-radius: 4px;
	}

	.marker-line {
		width: 2px;
		flex: 1;
		background: #ddd;
		min-height: 16px;
	}

	.timeline-content {
		flex: 1;
		padding-bottom: 1.25rem;
	}

	.entry-header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.entry-name {
		font-weight: 600;
		font-size: 0.95rem;
		color: #363636;
	}

	.entry-name.active {
		color: #ff3e00;
	}

	.badge {
		font-size: 0.65rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		padding: 0.15rem 0.5rem;
		border-radius: 3px;
	}

	.root-badge {
		color: #3273dc;
		background: rgba(50, 115, 220, 0.1);
	}

	.generating-badge {
		color: white;
		background: rgba(255, 62, 0, 0.85);
		animation: pulse 2s infinite;
	}

	@keyframes pulse {
		0%, 100% { opacity: 1; }
		50% { opacity: 0.5; }
	}

	.entry-date {
		font-size: 0.8rem;
		color: #999;
		display: block;
		margin-top: 2px;
	}

	/* Changes summary */
	.changes-summary {
		margin-top: 0.5rem;
		background: #f7fafc;
		border-radius: 4px;
		border: 1px solid #e2e8f0;
		padding: 0.5rem;
	}

	.change-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.25rem 0;
	}

	.change-row:not(:last-child) {
		border-bottom: 1px dashed #e2e8f0;
	}

	.change-table {
		font-size: 0.85rem;
		font-weight: 500;
		color: #4a5568;
	}

	.change-badges {
		display: flex;
		gap: 0.3rem;
	}

	.change-badge {
		font-size: 0.75rem;
		font-weight: 600;
		padding: 0.1rem 0.4rem;
		border-radius: 3px;
		font-variant-numeric: tabular-nums;
	}

	.change-badge.added {
		color: #22863a;
		background: rgba(34, 134, 58, 0.1);
	}

	.change-badge.removed {
		color: #cb2431;
		background: rgba(203, 36, 49, 0.1);
	}

	.change-badge.modified {
		color: #b08800;
		background: rgba(176, 136, 0, 0.1);
	}

	.change-totals {
		display: flex;
		gap: 0.3rem;
		justify-content: flex-end;
		padding-top: 0.35rem;
		margin-top: 0.25rem;
		border-top: 1px solid #e2e8f0;
	}

	/* Diff feature list */
	.diff-list-section {
		margin-top: 1rem;
		border-top: 1px solid #e2e8f0;
		padding-top: 0.75rem;
	}

	.diff-list-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 0.5rem 0.5rem;
	}

	.diff-list-title {
		font-weight: 600;
		font-size: 0.85rem;
		color: #4a5568;
	}

	.diff-list-count {
		font-size: 0.75rem;
		font-weight: 600;
		color: #667eea;
		background: rgba(102, 126, 234, 0.1);
		padding: 0.1rem 0.5rem;
		border-radius: 10px;
	}

	.diff-loading-indicator {
		width: 14px;
		height: 14px;
		border: 2px solid rgba(102, 126, 234, 0.2);
		border-top-color: #667eea;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}

	.diff-list {
		display: flex;
		flex-direction: column;
		gap: 1px;
		max-height: 40vh;
		overflow-y: auto;
	}

	.diff-item {
		display: flex;
		align-items: flex-start;
		gap: 0.5rem;
		padding: 0.4rem 0.5rem;
		font-size: 0.8rem;
		background: #fafafa;
	}

	.diff-item:hover {
		background: #f0f0f0;
	}

	.diff-status-icon {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
		font-weight: 700;
		font-size: 0.85rem;
		line-height: 16px;
		text-align: center;
		border-radius: 3px;
	}

	.diff-status-icon.added { color: #22863a; background: rgba(34, 134, 58, 0.1); }
	.diff-status-icon.removed { color: #cb2431; background: rgba(203, 36, 49, 0.1); }
	.diff-status-icon.modified { color: #b08800; background: rgba(176, 136, 0, 0.1); }

	.diff-item-info {
		flex: 1;
		min-width: 0;
	}

	.diff-item-table {
		font-weight: 600;
		color: #363636;
		margin-right: 0.3rem;
	}

	.diff-item-pkid {
		color: #718096;
		font-size: 0.75rem;
	}

	.diff-item-detail {
		display: flex;
		flex-wrap: wrap;
		gap: 0.25rem;
		margin-top: 2px;
	}

	.diff-field {
		font-size: 0.7rem;
		color: #4a5568;
		background: #edf2f7;
		padding: 0.1rem 0.35rem;
		border-radius: 2px;
	}

	.diff-item-type {
		font-size: 0.65rem;
		text-transform: uppercase;
		color: #a0aec0;
		flex-shrink: 0;
	}

	.diff-empty {
		text-align: center;
		padding: 1rem;
		color: #a0aec0;
		font-size: 0.85rem;
	}
</style>
